import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageSEO from '../components/PageSEO';
import {
  Syringe, TrendingUp, Users, Shield, CheckCircle, AlertTriangle,
  Baby, UserCheck, HeartHandshake, Target, Info, Sliders, RotateCcw, MapPin, Download, ExternalLink
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { InsightBox } from '../components/InsightBox';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { fetchWithFallback } from '../lib/dataFetcher';
import { SITE_MEDIA, VACCINE_GALLERY } from '../lib/siteMedia';
import { BRAND } from '../lib/brand';

const API = process.env.REACT_APP_BACKEND_URL;
const VACCINE_COLORS = { covid: '#0d9488', flu: '#f59e0b', childhood: '#6366f1', hpv: '#ec4899' };
const AGE_ICONS = { pediatric: Baby, adult: UserCheck, elderly: HeartHandshake };

const US_STATES = [
  { value: "US", label: "United States (National)" },
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "DC", label: "Washington DC" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg border border-slate-200 rounded-xl px-4 py-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (<p key={i} style={{ color: p.color || p.stroke }} className="font-medium">{p.name}: {p.value}%</p>))}
    </div>
  );
};

const CoverageGauge = ({ value, label, color, compareTo, target, description, stateLabel }) => {
  const level = value >= 70 ? 'Controlled' : value >= 50 ? 'Moderate' : 'Low';
  const levelColor = value >= 70 ? 'text-emerald-600' : value >= 50 ? 'text-amber-600' : 'text-red-600';
  const levelBg = value >= 70 ? 'bg-emerald-50 border-emerald-200' : value >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm text-center">
      <div className="relative w-24 h-24 mx-auto mb-2">
        <svg viewBox="0 0 36 36" className="w-full h-full">
          <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#f1f5f9" strokeWidth="3" />
          <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${value}, 100`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center"><span className="text-lg font-bold text-slate-900">{value}%</span></div>
      </div>
      <p className="font-semibold text-slate-900 text-sm">{label}</p>
      <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border mt-1 ${levelBg} ${levelColor}`}>{level}</span>
      <div className="mt-1.5 text-[10px] text-slate-400 space-y-0.5">
        <p>{stateLabel}: <strong className="text-slate-600">{compareTo}%</strong> | Target: <strong className="text-slate-600">{target}%</strong></p>
      </div>
    </div>
  );
};

export default function VaccineIntelligence() {
  const [vaccines, setVaccines] = useState({});
  const [ageData, setAgeData] = useState({});
  const [myths, setMyths] = useState([]);
  const [selectedVaccine, setSelectedVaccine] = useState('covid');
  const [selectedState, setSelectedState] = useState('US');
  const [simDrop, setSimDrop] = useState(10);
  const [expandedMyth, setExpandedMyth] = useState(null);

  useEffect(() => {
    fetchWithFallback(`/api/vaccines/coverage?state=${selectedState}`).then(d => d && setVaccines(d.vaccines || {})).catch(() => {});
  }, [selectedState]);

  useEffect(() => {
    fetchWithFallback('/api/vaccines/by-age').then(d => d && setAgeData(d.data || {})).catch(() => {});
    fetchWithFallback('/api/vaccines/myths').then(d => d && setMyths(d.myths || [])).catch(() => {});
  }, []);

  // Compute simulation client-side for instant slider response
  const simResult = useMemo(() => {
    const baseCoverage = { covid: 69.5, flu: 49.5, childhood: 92.7, hpv: 61.7 };
    const baseTargets = { covid: 85, flu: 70, childhood: 95, hpv: 80 };
    const base = vaccines[selectedVaccine] || {};
    const currentCoverage = baseCoverage[selectedVaccine] || base.us_coverage || 60;
    const target = baseTargets[selectedVaccine] || base.herd_immunity_target || 80;
    const newCoverage = Math.max(0, currentCoverage - simDrop);
    const gap = target - newCoverage;
    const caseIncrease = gap > 0 ? Math.round(gap * 1.8 * 10) / 10 : 0;
    const outbreakRisk = newCoverage < 50 ? 'Critical' : newCoverage < target - 15 ? 'High' : newCoverage < target ? 'Moderate' : 'Low';
    const name = base.name || selectedVaccine;
    return {
      vaccine: selectedVaccine,
      current_coverage: currentCoverage,
      simulated_coverage: Math.round(newCoverage * 10) / 10,
      target,
      gap_to_target: Math.round(Math.max(0, gap) * 10) / 10,
      projected_case_increase: `${caseIncrease}%`,
      outbreak_risk: outbreakRisk,
      impact_summary: `A ${simDrop}% drop in ${name} coverage would leave ${Math.round((100 - newCoverage) * 10) / 10}% unprotected, increasing case rates by ~${caseIncrease}%.`,
      vulnerable_populations: ['Infants too young for vaccines', 'Immunocompromised patients', 'Elderly with weakened immunity', 'Pregnant women'],
    };
  }, [vaccines, selectedVaccine, simDrop]);

  const currentVaccine = vaccines[selectedVaccine] || {};
  const trendData = currentVaccine.trend_data || [];
  const ageChartData = Object.entries(ageData).map(([key, val]) => ({ name: val.label, flu: val.flu, covid: val.covid, mmr: val.mmr, hpv: val.hpv || 0 }));
  const stateLabel = US_STATES.find(s => s.value === selectedState)?.label || selectedState;
  const trendStateKey = selectedState !== 'US' ? 'state' : 'ma';
  const getStateComparison = (v) => {
    if (selectedState !== 'US' && v.state_coverage != null) return v.state_coverage;
    return v.ma_coverage;
  };

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="vaccine-intelligence-page">
      <PageSEO title="Vaccine Intelligence" description="Explore vaccine coverage rates across all 50 US states. COVID-19, Flu, Childhood, and HPV vaccination data with trends, simulations, and myth-busting. Sourced from CDC NIS and WHO." path="/vaccines" />
      <section className="relative bg-white border-b border-slate-200/60 overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-12">
          <img src={SITE_MEDIA.vaccine} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center"><Syringe className="w-5 h-5 text-white" /></div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Vaccine Intelligence</h1>
            </div>
            <p className="text-base text-slate-500 max-w-2xl">Coverage rates, simulations, myth-busting, and age-group breakdowns. Every piece of data answers: what, why, and what you can do.</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* ========== DATA LINEAGE BANNER ========== */}
        <details className="rounded-2xl bg-teal-50 border border-teal-200 p-4 sm:p-5 group" data-testid="vaccine-data-lineage">
          <summary className="cursor-pointer flex items-start gap-3 list-none">
            <Info className="w-5 h-5 text-teal-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-teal-900">Data lineage — what's live, what's bundled, what's a reference figure</p>
              <p className="text-xs text-teal-800 mt-0.5">Click to expand · we want every number traceable to its source.</p>
            </div>
          </summary>
          <div className="mt-4 space-y-3 text-xs text-teal-900 leading-relaxed">
            <p><strong>✅ National COVID &amp; Flu coverage (latest week):</strong> LIVE from CDC's Weekly Respiratory Pathogens and Vaccination (RPV) dataset — <code className="text-[10px] bg-white/60 px-1.5 py-0.5 rounded">data.cdc.gov/resource/5c6r-xi2t</code>. National, demographic_level='Overall', latest week_ending.</p>
            <p><strong>⚠️ Per-state COVID, Flu, Kindergarten MMR, HPV coverage:</strong> BUNDLED ESTIMATES approximating the most-recent CDC published state coverage. They are NOT pulled live per state. For authoritative values use:</p>
            <ul className="ml-4 space-y-1">
              <li>• Adult Flu/COVID: <a href="https://www.cdc.gov/vaccines/imz-managers/coverage/adult-vaxview/index.html" target="_blank" rel="noopener noreferrer" className="underline">BRFSS Adult VaxView</a></li>
              <li>• Kindergarten MMR: <a href="https://www.cdc.gov/vaccines/imz-managers/coverage/schoolvaxview/" target="_blank" rel="noopener noreferrer" className="underline">SchoolVaxView</a></li>
              <li>• HPV ages 13–17: <a href="https://www.cdc.gov/vaccines/imz-managers/coverage/teenvaxview/" target="_blank" rel="noopener noreferrer" className="underline">TeenVaxView (NIS-Teen)</a></li>
            </ul>
            <p><strong>⚠️ State-trend lines:</strong> The state trend curve is derived by scaling the national CDC trend by the state-to-national ratio (deterministic — no random noise). Use it for relative shape, not absolute values; check the per-state CDC dashboards above for authoritative time series.</p>
            <p><strong>✅ Vaccine-by-age, herd-immunity simulation, myth-buster:</strong> Reference figures sourced from CDC vaccine education materials and peer-reviewed estimates. Each card links its underlying source.</p>
            <p><strong>🔗 Download the CSV</strong> for the full per-column methodology header and a <code>Data Status</code> column on every row.</p>
          </div>
        </details>

        {/* Hero image with vaccine needle */}
        <div className="relative rounded-2xl overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700 p-8 flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Vaccinations Save Lives</h2>
              <p className="text-base text-teal-100 leading-relaxed mb-4">Vaccines protect the person who gets the shot, reduce severe disease, and help shield infants, older adults, and immunocompromised people when coverage stays high.</p>
              <div className="flex gap-3 text-sm">
                <span className="px-3 py-1.5 bg-teal-600/50 rounded-full text-teal-100">4M+ lives saved annually</span>
                <span className="px-3 py-1.5 bg-teal-600/50 rounded-full text-teal-100">Proven science</span>
              </div>
            </div>
            <div className="h-64 md:h-auto bg-white">
              <img src={SITE_MEDIA.vaccineNeedle} alt="Vaccine needle close-up" className="w-full h-full object-contain bg-white p-4" />
            </div>
          </div>
        </div>

        {/* Vaccine Image Gallery - FULL IMAGES */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {VACCINE_GALLERY.map((img, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="rounded-xl overflow-hidden group relative bg-slate-100">
              <img src={img.src} alt={img.alt} className="w-full h-44 object-contain bg-slate-50 p-2" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-xs text-white">{img.caption}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Gauges */}

        {/* State Selector */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm" data-testid="vaccine-state-selector">
          <div className="flex items-center gap-3 mb-3">
            <MapPin className="w-5 h-5 text-teal-600" />
            <h3 className="text-base font-semibold text-slate-900">Select a State</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Compare vaccine coverage in your state vs the national average.</p>
          <select 
            value={selectedState} 
            onChange={e => setSelectedState(e.target.value)}
            className="w-full sm:w-72 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm bg-white"
            data-testid="vaccine-state-dropdown"
          >
            {US_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {selectedState !== 'US' && (
            <p className="text-xs text-teal-600 mt-2 font-medium">
              Showing {US_STATES.find(s => s.value === selectedState)?.label || selectedState} vaccine coverage compared to US averages.
            </p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-3" data-testid="why-vaccines-matter-cards">
          {[
            {
              title: 'They lower severe disease',
              body: 'Vaccines reduce hospitalization, complications, and death even when they do not prevent every infection.',
            },
            {
              title: 'They protect communities',
              body: 'High coverage makes it harder for viruses and bacteria to reach infants, older adults, and immunocompromised people.',
            },
            {
              title: 'They prevent downstream strain',
              body: 'Vaccination helps reduce emergency visits, time away from school or work, and pressure on hospitals during surges.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-2xl border border-teal-200/60 bg-white p-5 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700 mb-2">Why vaccines matter</p>
              <h3 className="text-base font-semibold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vaccine-gauges">
          {Object.entries(vaccines).map(([key, v]) => (
            <CoverageGauge key={key} value={v.us_coverage} label={v.name} color={VACCINE_COLORS[key]} compareTo={getStateComparison(v)} target={v.herd_immunity_target} stateLabel={stateLabel} />
          ))}
        </div>
        <InsightBox seeing={`Current US vaccination coverage for 4 key vaccines compared to ${stateLabel} and herd immunity targets.`} matters={`When coverage drops below targets (e.g., 95% for measles), outbreaks become possible. ${selectedState !== 'US' ? `${stateLabel}'s rates are shown for comparison.` : 'MA leads the nation in most categories.'}`} doAbout="Check your vaccination status. Visit vaccines.gov to find free vaccines near you." source="CDC Vaccination Coverage 2024" />

        {/* SIMULATION */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="vaccine-simulation">
          <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><Sliders className="w-5 h-5 text-red-500" /> What If Vaccination Rates Drop?</h2>
          <p className="text-xs text-slate-500 mb-4">Drag the slider to see the projected impact of declining coverage.</p>
          <div className="flex items-center gap-4 mb-5">
            <span className="text-sm font-medium text-slate-700 w-24">Drop by:</span>
            <input type="range" min="0" max="50" step="5" value={simDrop} onChange={e => setSimDrop(parseInt(e.target.value))}
              className="flex-1 accent-red-500" data-testid="sim-slider" />
            <span className="text-lg font-bold text-red-600 w-14 text-right">-{simDrop}%</span>
          </div>
          {simResult && (
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center">
                <p className="text-xs text-slate-500">Current Coverage</p>
                <p className="text-2xl font-bold text-slate-900">{simResult.current_coverage}%</p>
              </div>
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-center">
                <p className="text-xs text-red-600">Simulated Coverage</p>
                <p className="text-2xl font-bold text-red-700">{simResult.simulated_coverage}%</p>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center">
                <p className="text-xs text-amber-600">Case Increase</p>
                <p className="text-2xl font-bold text-amber-700">{simResult.projected_case_increase}</p>
              </div>
              <div className={`rounded-xl p-4 text-center border ${simResult.outbreak_risk === 'Critical' ? 'bg-red-100 border-red-300' : simResult.outbreak_risk === 'High' ? 'bg-red-50 border-red-200' : simResult.outbreak_risk === 'Moderate' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <p className="text-xs text-slate-600">Outbreak Risk</p>
                <p className="text-2xl font-bold">{simResult.outbreak_risk}</p>
              </div>
            </div>
          )}
          {simResult && <p className="text-sm text-slate-600 mt-4 bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">{simResult.impact_summary}</p>}
          {simResult?.vulnerable_populations && (
            <div className="mt-3 text-xs text-slate-500">
              <strong>Most vulnerable if rates drop:</strong> {simResult.vulnerable_populations.join(' · ')}
            </div>
          )}
        </div>

        {/* Selector + Trend */}
        <div className="flex flex-wrap gap-2" data-testid="vaccine-selector">
          {Object.entries(vaccines).map(([key, v]) => (
            <button key={key} onClick={() => setSelectedVaccine(key)} data-testid={`vaccine-btn-${key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${key === selectedVaccine ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
              <Syringe className="w-4 h-4" /> {v.name}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="vaccine-trend-chart">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-teal-600" /> {currentVaccine.name || ''} — Coverage Over Time</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => String(v)} />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" domain={[0, 100]} unit="%" />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="us" name="US Average" stroke="#94a3b8" strokeWidth={2.5} dot={{ r: 4 }} />
              <Line type="monotone" dataKey={trendStateKey} name={stateLabel} stroke={VACCINE_COLORS[selectedVaccine]} strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
          <InsightBox seeing={`${currentVaccine.name || ''} vaccination trends comparing US average to ${stateLabel}.`} matters={`${stateLabel} ${selectedState !== 'US' ? 'is shown for state-level comparison' : 'leads in vaccination'} — but even small dips can trigger outbreaks. Every percentage point of coverage protects thousands.`} doAbout="Stay current on boosters. Check vaccines.gov for your state's availability." source="CDC Vaccination Coverage 2024" />
        </div>

        {/* Age */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="vaccine-age-chart">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Coverage by Age Group</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={ageChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" domain={[0, 100]} unit="%" />
              <Tooltip content={<ChartTooltip />} /><Legend />
              <Bar dataKey="flu" name="Flu" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="covid" name="COVID" fill="#0d9488" radius={[4, 4, 0, 0]} />
              <Bar dataKey="mmr" name="MMR" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="hpv" name="HPV" fill="#ec4899" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <InsightBox seeing="How vaccination rates differ across age groups for 4 key vaccines." matters="Seniors lead in COVID/flu vaccination. Children have high MMR but low HPV. Adults are the biggest gap — only 41% get annual flu shots." doAbout="Check which vaccines are recommended for YOUR age group at cdc.gov/vaccines." source="CDC 2024" />
        </div>

        {/* HERD IMMUNITY VISUAL */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6" data-testid="herd-immunity-section">
          <h2 className="text-lg font-semibold text-teal-800 mb-3 flex items-center gap-2"><Shield className="w-5 h-5" /> Herd Immunity: Protecting Everyone</h2>
          <p className="text-sm text-teal-700 mb-4">When enough people are immune, disease can't spread easily — even to those who can't be vaccinated (infants, immunocompromised, elderly). This "community shield" is called herd immunity.</p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[{ disease: 'Measles', threshold: '95%', status: 'Near threshold' }, { disease: 'COVID-19', threshold: '85%', status: 'Below threshold' }, { disease: 'Flu', threshold: '70%', status: 'Below threshold' }].map((d, i) => (
              <div key={i} className="bg-white rounded-xl border border-teal-200 p-4 text-center">
                <p className="text-sm font-bold text-slate-900">{d.disease}</p>
                <p className="text-2xl font-bold text-teal-700 my-1">{d.threshold}</p>
                <p className="text-xs text-slate-500">{d.status}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-teal-600">When communities fall below these thresholds, vulnerable populations — babies, cancer patients on chemotherapy, organ transplant recipients — lose their community protection.</p>
        </div>

        {/* MYTH vs FACT */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="myth-fact-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-red-500" /> Myth vs Fact</h2>
          <div className="space-y-3">
            {myths.map(m => (
              <div key={m.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`myth-${m.id}`}>
                <button onClick={() => setExpandedMyth(expandedMyth === m.id ? null : m.id)} className="w-full p-4 text-left flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <span className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold shrink-0">M</span>
                  <p className="text-sm font-semibold text-slate-900 flex-1">{m.myth}</p>
                  <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Tap for fact</span>
                </button>
                <AnimatePresence>
                  {expandedMyth === m.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-slate-100">
                      <div className="p-4 bg-emerald-50 flex items-start gap-3">
                        <span className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold shrink-0">F</span>
                        <div>
                          <p className="text-sm text-slate-800 leading-relaxed">{m.fact}</p>
                          <p className="text-[10px] text-slate-500 mt-2">Source: {m.source}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* Data Sources & Download */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="vaccine-data-sources">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Info className="w-5 h-5 text-teal-600" /> Data Sources & Download</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Where this data comes from:</p>
              <ul className="space-y-2">
                {[
                  { name: "CDC National Immunization Survey (NIS)", url: "https://www.cdc.gov/vaccines/imz-managers/nis/index.html" },
                  { name: "CDC Vaccination Coverage Reports", url: "https://www.cdc.gov/vaccines/vaxview/index.html" },
                  { name: "WHO Global Vaccine Coverage", url: "https://www.who.int/data/gho/data/themes/immunization" },
                  { name: "The Lancet Vaccine Studies", url: "https://www.thelancet.com/journals/lancet/home" },
                ].map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 hover:text-teal-700 transition-colors">
                      {s.name} <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Download Data:</p>
              <p className="text-xs text-slate-500 mb-3">Download state-level vaccine coverage data as CSV for your own analysis.</p>
              <CsvDownloadButton dataset="vaccine" disease="all" label="Download Vaccine Data (CSV)" />
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center py-4">Sources: CDC Vaccination Coverage 2024 · The Lancet · WHO</p>
      </div>
    </div>
  );
}
